import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Skeleton,
  useTheme,
  useMediaQuery,
  Checkbox,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import Unauthorized from "screens/Shared/Unauthorized";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import moment from "moment";
import CustomAutoCompleteNew from "global/components/CustomAutoCompleteNew/CustomAutoCompleteNew";
import EnvelopeProgressGraph from "screens/Insights/components/EnvelopeProgressGraph";
import TotalDocumentProgressGraph from "screens/Insights/components/TotalDocumentProgressGraph";
import {
  fetchEnvelopeGraphData,
  fetchTotalProgressData,
} from "screens/Insights/InsightsHelper";
import { visuallyHidden } from "@mui/utils";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";

const STATUS_FILTER_ID = "document-progress-status-filter";

const EnvelopesReport = () => {
  useTitle(title.DOCUMENT_PROGRESS);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // `isMobile` is the md breakpoint, which still leaves the two filter controls
  // side by side down to 320px. Reflow (WCAG 1.4.10) needs them stacked before
  // that point.
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const [totalProgressData, setTotalProgressData] = useState<any>(null);

  const [openDateRangePicker, setOpenDateRangePicker] = useState(false);
  const [dateRange, setDateRange] = useState<any>({
    fromDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  const [pickerDateRange, setPickerDateRange] = useState<any>(dateRange);

  const maxDate = moment();
  const statusOptions = [
    // { label: "All", value: "all" },
    { label: "Requested", value: "requested" },
    { label: "Sent", value: "sent" },
    { label: "Opened", value: "opened" },
    { label: "Completed", value: "completed" },
  ];

  const [selectedStatus, setSelectedStatus] = useState<any[]>(statusOptions);

  // const getDefaultRange = ()=>({
  //   // fromDate : moment().subtract(1,"month").format("YYYY-MM-DD"),
  //   // toDate : moment().format("YYYY-MM-DD")
  //   fromDate: "2026-01-01",
  //   toDate: "2026-03-01"
  // })

  const hasFetchPermission = hasAccessTo(
    strings.resources.report,
    strings.fetchPermission,
  );

  useEffect(() => {
    if (hasFetchPermission && dateRange.fromDate && dateRange.toDate) {
      setIsLoading(true);
      const activeMetrics = selectedStatus.map((s) => s.value);
      Promise.all([
        fetchEnvelopeGraphData(
          dateRange.fromDate,
          dateRange.toDate,
          activeMetrics,
          () => {},
          setChartData,
        ),
        fetchTotalProgressData(
          dateRange.fromDate,
          dateRange.toDate,
          // selectedStatus.value || "all",
          setTotalProgressData,
        ),
      ]).finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [hasFetchPermission, dateRange, selectedStatus]);

  const renderHeader = () => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems={isMobile ? "flex-start" : "center"}
      mb={3}
      // Lets the filter block drop below the title instead of forcing a
      // horizontal scrollbar at narrow widths / 400% zoom (WCAG 1.4.10).
      // `gap` rather than Stack's `spacing`: spacing is a left margin, which a
      // full-width wrapped child would add to 100% and overflow again.
      sx={{ flexWrap: "wrap", gap: 2 }}
    >
      {/* Title Section */}
      <Stack spacing={1}>
        <Typography
          variant="h1"
          fontSize="1.6667rem !important"
          color="text.primary"
          fontWeight={600}
        >
          Document progress
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track how documents are moving through the workflow funnel.
        </Typography>
      </Stack>

      {/* Filter section */}
      <Stack
        direction={isNarrow ? "column" : "row"}
        alignItems={isNarrow ? "stretch" : "center"}
        sx={{ width: isMobile ? "100%" : "auto", gap: 2, flexWrap: "wrap" }}
      >
        {/* List of status  */}
        <Box
          sx={{
            // `calc` leaves room for the 16px gap, so two half-width controls
            // still fit inside the 100%-wide row (WCAG 1.4.10).
            width: isNarrow ? "100%" : isMobile ? "calc(50% - 8px)" : 200,
          }}
        >
          <CustomAutoCompleteNew
            multiple
            label="Filter by Status"
            // The label is tied to this id by `CustomAutoCompleteNew`, so it has
            // to describe the control it actually labels (WCAG 1.3.1 / 4.1.2).
            id={STATUS_FILTER_ID}
            requiredIndicator={false}
            options={statusOptions}
            value={selectedStatus}
            disableCloseOnSelect
            limitTags={1}
            size="small"
            getOptionLabel={(option: any) => option.label || ""}
            renderOption={(props: any, option: any, { selected }: any) => (
              <li {...props} style={{ padding: "4px 8px" }}>
                {/* The option's own `aria-selected` already reports the state,
                    and a focusable control nested inside a listbox option is
                    unreachable by keyboard — so the box is presentational
                    (WCAG 2.1.1 / 4.1.2). */}
                <Checkbox
                  size="small"
                  style={{ marginRight: 8, padding: 4 }}
                  checked={selected}
                  tabIndex={-1}
                  disableRipple
                  aria-hidden="true"
                />
                {option.label}
              </li>
            )}
            onChange={(event: any, newValue: any) => {
              if (newValue) {
                setSelectedStatus(newValue);
              }
            }}
          />
        </Box>

        {/* Date range picker */}
        <Box
          sx={{
            width: isNarrow ? "100%" : isMobile ? "calc(50% - 8px)" : 380,
            minWidth: 0,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "4px",
            px: 1,
            backgroundColor: "background.paper",
          }}
        >
          <CustomDateRangePicker
            ariaLabel="Document progress date range"
            isClearable={false}
            openDateRangePicker={openDateRangePicker}
            setOpenDateRangePicker={setOpenDateRangePicker}
            dateRange={pickerDateRange}
            setDateRange={setPickerDateRange}
            helperFunction={(confirmedRange: any) => {
              // if (!confirmedRange.fromDate || !confirmedRange.toDate) {
              //   const defaultRange = getDefaultRange();
              //   setDateRange(defaultRange);
              //   setPickerDateRange(defaultRange);
              // } else {
              setDateRange(confirmedRange);
              //}
            }}
            // maxDate={maxDate}
            disableFuture={false}
          />
        </Box>
      </Stack>
    </Stack>
  );

  const renderSkeleton = () => (
    <Stack spacing={2}>
      <Skeleton variant="text" width="25%" height={50} />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={500}
        sx={{ borderRadius: 2, mt: 3 }}
      />
      <Skeleton
        variant="rectangular"
        width="100%"
        height={300}
        sx={{ borderRadius: 2, mt: 3 }}
      />
    </Stack>
  );

  return (
    <>
      {hasFetchPermission ? (
        <Box
          sx={{
            width: "100%",
            pb: isMobile ? 2 : 4,
          }}
        >
          <Stack direction="column" spacing={2}>
            {/* {renderSkeleton()} */}
            {renderHeader()}
            {/* The skeletons are invisible to assistive tech, so the swap to
                real charts is announced instead (WCAG 4.1.3). */}
            <Box role="status" aria-live="polite" sx={visuallyHidden}>
              {isLoading
                ? "Loading document progress charts."
                : "Document progress charts updated."}
            </Box>
            {isLoading ? (
              <Stack spacing={2} aria-hidden="true">
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={500}
                  sx={{ borderRadius: 2 }}
                />
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={300}
                  sx={{ borderRadius: 2 }}
                />
              </Stack>
            ) : (
              <Box sx={{ width: "100%" }}>
                {/* Rendering refactored components */}
                <EnvelopeProgressGraph
                  data={chartData}
                  isMobile={isMobile}
                  selectedMetrics={selectedStatus.map((s) => s.value)}
                />
                <TotalDocumentProgressGraph
                  data={totalProgressData}
                  isMobile={isMobile}
                  // selectedMetrics={selectedStatus.value || "all"}
                />
              </Box>
            )}
          </Stack>
        </Box>
      ) : (
        <Unauthorized permissionDenied={!hasFetchPermission} />
      )}
    </>
  );
};

export default EnvelopesReport;
